/**
 * Google Analytics 4 Event Tracking Utility & Privacy-First Analytics
 * Provides type-safe event tracking for GA4 and GDPR-compliant tracking via Supabase
 *
 * Event naming follows GA4 recommended conventions for future Google Ads import.
 */

import { supabase } from "@/integrations/supabase/client";
import { isMarketingAudience, isMarketingPath, isStaffSession } from "@/lib/marketingAudience";
import { isProductionAnalyticsHost } from "@/lib/ga4";
import { analyticsAllowed } from '@/lib/consent';
import { lifecycleEvent } from '../../supabase/functions/_shared/advisorLifecycle';

// Window.gtag and Window.dataLayer are declared in src/types/global.d.ts

// ---------------------------------------------------------------------------
// Core GA4 event helper
// ---------------------------------------------------------------------------

/**
 * Send a custom event to GA4.
 * All events are silently dropped if gtag is not loaded.
 */
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  if (lifecycleEvent(eventName,params)) void trackAdvisorLifecycle(eventName,params);
  // gtag may already be loaded from a public page; a staff member who then
  // opens the portal must not keep sending marketing events.
  if (typeof window !== 'undefined' && window.gtag && isMarketingAudience(isProductionAnalyticsHost())) {
    window.gtag('event', eventName, params);
  }
};

/** First-party product events, never pageviews/conversions or transcript analytics. */
export async function trackAdvisorLifecycle(name:string,params?:Record<string,unknown>):Promise<boolean> {
  try {
    const event=lifecycleEvent(name,params);
    if(!event || typeof window==='undefined' || !analyticsAllowed() || isStaffSession() || !isProductionAnalyticsHost() || params?.isTest===true || params?.is_test===true)return false;
    const path=window.location.pathname;
    const surface=/^\/customer(?:\/|$)/.test(path)?'home_hub':name.startsWith('homeowner_signup')?'signup':'public';
    const result=await supabase.functions.invoke('track-analytics',{body:{operation:'advisor_lifecycle',eventName:event.event,metadata:{...event.meta,surface},pageUrl:path,sessionId:getSessionId(),analyticsConsent:true}});
    return !result.error && result.data?.success===true && result.data?.skipped!==true;
  } catch { return false; }
}

// ---------------------------------------------------------------------------
// Canonical lead events — one name per business action, de-duplicated and
// scrubbed of personal data before anything reaches GA4.
// ---------------------------------------------------------------------------

export const LEAD_EVENTS = [
  'enquiry_submit',
  'consultation_booked',
  'quote_started',
  'quote_submitted',
  'whatsapp_click',
  'phone_click',
  'contact_email_click',
  'homeowner_account_created',
  'service_cta_click',
] as const;
export type LeadEventName = (typeof LEAD_EVENTS)[number];

/** Parameter keys that must never be sent to GA4. */
const PII_KEYS = /(^|_)(email|email_address|phone|phone_number|mobile|name|full_name|first_name|last_name|address|street|message|notes)$/i;
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const PHONE_RE = /\+?\d[\d\s()-]{8,}\d/;

/** Reduce a postcode to its outward area (e.g. "SO22 6FD" -> "SO22"). */
export function postcodeArea(postcode?: string | null): string | undefined {
  if (!postcode) return undefined;
  const clean = postcode.toUpperCase().replace(/\s+/g, '');
  if (clean.length < 5) return /^[A-Z]{1,2}\d[A-Z\d]?$/.test(clean) ? clean : undefined;
  const outward = clean.slice(0, -3);
  return /^[A-Z]{1,2}\d[A-Z\d]?$/.test(outward) ? outward : undefined;
}

export function scrubLeadParams(params: Record<string, unknown> = {}): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (k === 'postcode' || k === 'postcode_area') {
      const area = postcodeArea(String(v));
      if (area) out.postcode_area = area;
      continue;
    }
    if (PII_KEYS.test(k)) continue;
    if (typeof v === 'string') {
      if (EMAIL_RE.test(v) || PHONE_RE.test(v)) continue;
      out[k] = v.slice(0, 100);
    } else if (typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}

const recentLeadEvents = new Map<string, number>();
const LEAD_EVENT_WINDOW_MS = 2000;
/** Events that should count once per browser session (start-of-funnel). */
const ONCE_PER_SESSION: ReadonlySet<LeadEventName> = new Set(['quote_started']);
/** Click events mirrored to first-party analytics so tracking health is measurable. */
const FIRST_PARTY_MIRROR: ReadonlySet<LeadEventName> = new Set([
  'whatsapp_click', 'phone_click', 'contact_email_click', 'homeowner_account_created',
]);

/** Returns true when the event was sent (false when suppressed as a duplicate). */
export const trackLeadEvent = (name: LeadEventName, params?: Record<string, unknown>): boolean => {
  if (typeof window === 'undefined') return false;
  const page_path = window.location.pathname;
  const clean = scrubLeadParams({ ...params, page_path });
  const key = `${name}|${page_path}|${clean.cta_label ?? clean.click_label ?? ''}|${clean.service ?? ''}`;
  const now = Date.now();
  const last = recentLeadEvents.get(key);
  if (last && now - last < LEAD_EVENT_WINDOW_MS) return false;
  if (ONCE_PER_SESSION.has(name)) {
    try {
      const sk = `_evo_le_${name}_${clean.service ?? page_path}`;
      if (sessionStorage.getItem(sk)) return false;
      sessionStorage.setItem(sk, '1');
    } catch { /* storage unavailable — window de-dupe still applies */ }
  }
  recentLeadEvents.set(key, now);
  trackEvent(name, clean);
  if (FIRST_PARTY_MIRROR.has(name) && isMarketingPath(page_path) && !isStaffSession()) {
    void trackConversion(name);
  }
  return true;
};

/** Service/location pages whose CTA clicks count as service_cta_click. */
export function serviceSlugFromPath(path: string): string | undefined {
  const m = path.match(/^\/services\/([^/?#]+)/) || path.match(/^\/locations\/[^/]+\/([^/?#]+)/);
  return m?.[1];
}

const CTA_TARGETS = /^\/(quote|book|book-consultation|contact|remote-quote|instant-budget|free-homeowner-account|services\/[^/]+\/(quote|remote))/;

/**
 * One delegated listener for service/location pages: any click on a link or
 * button leading to an enquiry/quote/booking route fires service_cta_click.
 * Avoids editing every CTA and cannot double-bind.
 */
let serviceCtaBound = false;
export function initServiceCtaTracking(): void {
  if (serviceCtaBound || typeof document === 'undefined') return;
  serviceCtaBound = true;
  document.addEventListener('click', (e) => {
    const service = serviceSlugFromPath(window.location.pathname);
    if (!service) return;
    const el = (e.target as HTMLElement | null)?.closest?.('a,button') as HTMLElement | null;
    if (!el) return;
    const href = el.getAttribute('href') || '';
    let path = '';
    try { path = href ? new URL(href, window.location.origin).pathname : ''; } catch { path = ''; }
    const isCta = (path && CTA_TARGETS.test(path)) || el.dataset.leadCta !== undefined;
    if (!isCta) return;
    const label = (el.getAttribute('aria-label') || el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60);
    trackLeadEvent('service_cta_click', { service, cta_label: label, cta_target: path || 'button' });
  }, { capture: true, passive: true });
}

// ---------------------------------------------------------------------------
// Page-level events
// ---------------------------------------------------------------------------

/** Fire a GA4 page_view for SPA route changes (called from ScrollToTop) */
export const trackGA4Pageview = (path: string, title?: string) => {
  trackEvent('page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.origin + path,
  });
};

/** Track service page views with commercial context */
export const trackServicePageView = (serviceSlug: string, serviceTitle: string) => {
  trackEvent('service_page_view', {
    page_type: 'service',
    service_name: serviceTitle,
    service_slug: serviceSlug,
    page_location: window.location.href,
  });
};

/** Track location-service page views with geographic context */
export const trackLocationPageView = (
  locationName: string,
  serviceSlug?: string,
  serviceTitle?: string
) => {
  trackEvent('location_page_view', {
    page_type: serviceSlug ? 'location_service' : 'location_county',
    location_name: locationName,
    ...(serviceSlug && { service_slug: serviceSlug }),
    ...(serviceTitle && { service_name: serviceTitle }),
    page_location: window.location.href,
  });
};

/** Track blog post views for content performance */
export const trackBlogPostView = (postTitle: string, category: string, postId: string) => {
  trackEvent('blog_post_view', {
    page_type: 'blog',
    content_title: postTitle,
    content_category: category,
    content_id: postId,
    page_location: window.location.href,
  });
};

// ---------------------------------------------------------------------------
// Lead-generation & conversion events
// ---------------------------------------------------------------------------

/**
 * Fire the GA4 `generate_lead` recommended event.
 * This is the primary conversion event for Google Ads import.
 */
/**
 * Estimated lead values by type (GBP) for GA4 conversion value reporting.
 * These help Google Ads Smart Bidding optimise for higher-value conversions.
 */
const LEAD_VALUES: Record<string, number> = {
  quote: 50,
  remote_ashp: 75,
  contact: 25,
  callback: 20,
  ai_advisor: 15,
  consultation: 35,
  installer: 10,
  consultant: 10,
  partner: 15,
};

export const trackGenerateLead = (
  leadType: string,
  extra?: { service_type?: string; location_name?: string; isTest?: boolean }
) => {
  trackEvent('generate_lead', {
    lead_type: leadType,
    currency: 'GBP',
    value: LEAD_VALUES[leadType] ?? 20,
    ...(extra?.service_type && { service_type: extra.service_type }),
    ...(extra?.location_name && { location_name: extra.location_name }),
    ...(extra?.isTest === true && { is_test: true, debug_mode: true }),
    page_location: window.location.href,
  });
};

/**
 * Track form submission events — fires unified `form_submit` key event.
 * Also fires `generate_lead` for lead-generating forms.
 *
 * When `extra.isTest === true`, the GA4 events still fire but are tagged with
 * `is_test: true` + `debug_mode: true` so they can be filtered out of GA4
 * reporting (and they are harmless to GA4 numbers compared to Meta/Ads).
 */
export const trackFormSubmit = (
  formName: 'contact' | 'quote' | 'remote_ashp' | 'installer' | 'consultant' | 'partner',
  extra?: { service_type?: string; postcode?: string; isTest?: boolean; lead_event?: LeadEventName }
) => {
  const area = postcodeArea(extra?.postcode);
  trackEvent('form_submit', {
    form_name: formName,
    page_location: window.location.href,
    ...(extra?.service_type && { service_type: extra.service_type }),
    ...(area && { postcode_area: area }),
    ...(extra?.isTest === true && { is_test: true, debug_mode: true }),
  });

  // Canonical lead event for this submission.
  const leadEvent: LeadEventName | undefined = extra?.lead_event
    ?? (formName === 'contact' ? 'enquiry_submit'
      : formName === 'quote' || formName === 'remote_ashp' ? 'quote_submitted'
      : undefined);
  if (leadEvent) {
    trackLeadEvent(leadEvent, {
      service: extra?.service_type,
      postcode_area: area,
      form_name: formName,
      ...(extra?.isTest === true && { is_test: true, debug_mode: true }),
    });
  }

  // Lead-generating forms also fire generate_lead
  const leadForms = ['contact', 'quote', 'remote_ashp'];
  if (leadForms.includes(formName)) {
    trackGenerateLead(formName, { service_type: extra?.service_type, isTest: extra?.isTest });
  }
};


/** Track when a user first interacts with a form (focus on first field) */
export const trackFormStart = (formName: string, service?: string) => {
  trackEvent('form_start', {
    form_name: formName,
    page_location: window.location.href,
  });
  if (/quote|remote_ashp/i.test(formName)) {
    trackLeadEvent('quote_started', { form_name: formName, service });
  }
};

/** Track form submission errors for funnel diagnostics */
export const trackFormError = (formName: string, errorMessage?: string, errorCode?: string) => {
  trackEvent('form_submit_error', {
    form_name: formName,
    error_message: errorMessage || 'validation_error',
    ...(errorCode && /^[0-9A-Z]{5}$/.test(errorCode) ? { form_error_code: errorCode } : {}),
    page_location: window.location.origin + window.location.pathname,
  });
};

// ---------------------------------------------------------------------------
// Engagement events
// ---------------------------------------------------------------------------

/**
 * Track phone click events — fires GA4 `phone_click` + Meta Pixel/CAPI
 * `Contact` event so paid platforms can optimise on call intent.
 *
 * Phone/WhatsApp clicks are the highest-intent micro-conversions on this
 * site (lead form fills aside) — mirroring them as Meta `Contact` is what
 * unlocks call-focused paid optimisation.
 */
export const trackPhoneClick = (label: string, phoneNumber?: string) => {
  void phoneNumber; // business number only; never forwarded to GA4
  if (!trackLeadEvent('phone_click', { click_label: label, service: serviceSlugFromPath(window.location.pathname) })) return;
  // Fire Meta Contact (browser Pixel + CAPI mirror). Safe no-op if Pixel
  // is not configured or ad consent has not been granted.
  void import('./metaPixelEvents').then(({ trackMetaConversion }) => {
    trackMetaConversion('callback_request', {
      channel: 'phone',
      click_label: label,
      page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  }).catch(() => { /* never block UX */ });
};

/**
 * Track email link click events
 */
export const trackEmailClick = (label: string, emailAddress?: string) => {
  void emailAddress; // never forwarded to GA4
  trackEvent('email_click', {
    page_location: window.location.href,
    click_label: label,
  });
  trackLeadEvent('contact_email_click', { click_label: label, service: serviceSlugFromPath(window.location.pathname) });
};

/**
 * Track WhatsApp click events — fires GA4 `whatsapp_click` + Meta
 * Pixel/CAPI `Contact` so WhatsApp click campaigns can optimise on it.
 */
export const trackWhatsAppClick = (label: string) => {
  if (!trackLeadEvent('whatsapp_click', { click_label: label, service: serviceSlugFromPath(window.location.pathname) })) return;
  void import('./metaPixelEvents').then(({ trackMetaConversion }) => {
    trackMetaConversion('callback_request', {
      channel: 'whatsapp',
      click_label: label,
      page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  }).catch(() => { /* never block UX */ });
};

/**
 * Track thank-you page view
 */
export const trackThankYouPageView = (formName?: string) => {
  trackEvent('thank_you_page_view', {
    page_location: window.location.href,
    form_name: formName || 'unknown',
  });
};

/**
 * Legacy wrappers — kept for backward compatibility, now route through new functions
 */
export const trackButtonClick = (buttonType: 'phone' | 'whatsapp' | 'primary_cta' | 'quote_cta' | 'contact_cta', label?: string) => {
  if (buttonType === 'phone') {
    trackPhoneClick(label || buttonType);
    return;
  }
  if (buttonType === 'whatsapp') {
    trackWhatsAppClick(label || buttonType);
    return;
  }
  // Other CTA clicks — keep as generic events
  trackEvent('cta_click', {
    button_type: buttonType,
    button_label: label,
    page_location: window.location.href,
  });
};

/**
 * Generic CTA-click tracker — used for high-intent links (e.g. specialist survey
 * "Book this survey", planner step starts) where a full form_submit has not yet
 * fired. Reuses the existing `cta_click` event and adds a stable `cta_label`
 * + `cta_location` so GA4 reporting can group by surface.
 *
 * Keep usage to KEY conversion paths — do NOT attach to every UI button.
 */
export const trackCtaClick = (label: string, location: string, extra?: Record<string, any>) => {
  trackEvent('cta_click', {
    cta_label: label,
    cta_location: location,
    page_location: typeof window !== 'undefined' ? window.location.href : '',
    ...(extra ?? {}),
  });
  const service = typeof window !== 'undefined' ? serviceSlugFromPath(window.location.pathname) : undefined;
  if (service) trackLeadEvent('service_cta_click', { service, cta_label: label, cta_location: location });
};

/**
 * Multi-step planner start — fired ONCE per planner mount when the user first
 * advances past the initial step. Helps measure top-of-funnel engagement.
 */
export const trackPlannerStart = (plannerType: string = 'home_improvement') => {
  trackEvent('planner_start', {
    planner_type: plannerType,
    page_location: typeof window !== 'undefined' ? window.location.href : '',
  });
};

/**
 * Track scroll depth on key pages
 */
export const trackScrollDepth = (depth: 25 | 50 | 75 | 100, pageType: string) => {
  trackEvent('scroll_depth', {
    depth_percentage: depth,
    page_type: pageType,
  });
};

/**
 * Initialize scroll depth tracking for a page
 */
export const initScrollTracking = (pageType: string) => {
  if (typeof window === 'undefined') return () => {};

  const thresholds = [25, 50, 75, 100];
  const triggered = new Set<number>();

  const handleScroll = () => {
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollHeight <= 0) return;
    const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);

    thresholds.forEach((threshold) => {
      if (scrollPercent >= threshold && !triggered.has(threshold)) {
        triggered.add(threshold);
        trackScrollDepth(threshold as 25 | 50 | 75 | 100, pageType);
      }
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
};

// ---------------------------------------------------------------------------
// Privacy-First Analytics Tracking (No cookies, GDPR compliant)
// Lightweight tracking via Supabase without external services
// ---------------------------------------------------------------------------

const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('_sid');
  if (!sessionId) {
    sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('_sid', sessionId);
  }
  return sessionId;
};

const getDeviceType = (): string => {
  return /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
};

const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  return 'Other';
};

const extractSlugsFromUrl = (url: string): { serviceSlug?: string; locationSlug?: string } => {
  const path = url || window.location.pathname;
  
  const serviceMatch = path.match(/^\/services\/([^/]+)/);
  if (serviceMatch) {
    return { serviceSlug: serviceMatch[1] };
  }
  
  const locationServiceMatch = path.match(/^\/locations\/([^/]+)\/([^/]+)/);
  if (locationServiceMatch) {
    return { locationSlug: locationServiceMatch[1], serviceSlug: locationServiceMatch[2] };
  }
  
  const locationMatch = path.match(/^\/locations\/([^/]+)$/);
  if (locationMatch) {
    return { locationSlug: locationMatch[1] };
  }
  
  return {};
};

/** Capture UTM params on first visit; persist for the session. */
const getSessionUtm = (): { utmSource?: string; utmMedium?: string; utmCampaign?: string } => {
  try {
    const KEY = '_evo_utm';
    const existing = sessionStorage.getItem(KEY);
    if (existing) return JSON.parse(existing);
    const sp = new URLSearchParams(window.location.search);
    const out: Record<string, string> = {};
    const s = sp.get('utm_source'); if (s) out.utmSource = s.slice(0, 100);
    const m = sp.get('utm_medium'); if (m) out.utmMedium = m.slice(0, 100);
    const c = sp.get('utm_campaign'); if (c) out.utmCampaign = c.slice(0, 200);
    sessionStorage.setItem(KEY, JSON.stringify(out));
    return out;
  } catch { return {}; }
};

/**
 * Track pageview via Supabase (privacy-first, no cookies)
 */
/**
 * Actor exclusion for first-party marketing metrics: a signed-in staff member
 * browsing a public page (or entering an enquiry for a customer through a
 * public form) is not marketing traffic. Portal pages are still recorded and
 * bucketed as internal by the reports; the enquiry itself is always saved.
 */
function isInternalPublicVisit(path: string): boolean {
  return isStaffSession() && isMarketingPath(path);
}

export const trackPageview = async (pageUrl?: string, pageTitle?: string) => {
  try {
    const url = pageUrl || window.location.pathname;
    if (isInternalPublicVisit(url)) return;
    const { serviceSlug, locationSlug } = extractSlugsFromUrl(url);
    const utm = getSessionUtm();

    await supabase.functions.invoke('track-analytics', {
      body: {
        pageUrl: url,
        pageTitle: pageTitle || document.title,
        referrer: document.referrer || null,
        deviceType: getDeviceType(),
        browser: getBrowser(),
        sessionId: getSessionId(),
        isConversion: false,
        serviceSlug,
        locationSlug,
        ...utm,
      }
    });
  } catch {
    // Silent fail — analytics must never break the site
  }
};
/**
 * Track conversion via Supabase (privacy-first).
 *
 * Pass `{ isTest: true }` to mark the row as a test in `site_analytics.is_test`.
 * Production behaviour is unchanged when `isTest` is omitted or false.
 */
export const trackConversion = async (
  conversionType: string,
  opts?: { isTest?: boolean }
) => {
  try {
    if (isInternalPublicVisit(window.location.pathname)) return;
    const utm = getSessionUtm();
    await supabase.functions.invoke('track-analytics', {
      body: {
        pageUrl: window.location.pathname,
        pageTitle: document.title,
        referrer: document.referrer || null,
        deviceType: getDeviceType(),
        browser: getBrowser(),
        sessionId: getSessionId(),
        isConversion: true,
        conversionType,
        isTest: opts?.isTest === true,
        ...utm,
      }
    });
  } catch {
    // Silent fail
  }
};



/**
 * Track both GA4 and Supabase pageview on SPA route change.
 * Called from ScrollToTop on every pathname change.
 */
export const trackRouteChange = (path: string) => {
  // GA4 page_view
  trackGA4Pageview(path);
  // Supabase privacy-first pageview
  trackPageview(path);
};

/**
 * Initialize Supabase analytics on app load.
 * Tracks initial pageview only — SPA route changes are handled by ScrollToTop.
 */
export const initAnalytics = () => {
  initServiceCtaTracking();
  trackPageview();
};
